<template>
  <div class="nb-dashboard-guia pb-5">
    <!-- HERO / ONBOARDING PROGRESS BANNER -->
    <div class="nb-hero-card mb-4">
      <div class="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3">
        <div class="d-flex flex-column">
          <div class="d-flex align-items-center mb-1">
            <div class="nb-hero-icon mr-2">
              <i class="fa fa-rocket text-primary"></i>
            </div>
            <h3 class="nb-hero-title mb-0">Guia de Primeiros Passos - Força de Vendas</h3>
          </div>
          <p class="nb-hero-subtitle mb-0">
            Siga as 4 etapas essenciais abaixo para configurar sua empresa, cadastrar produtos e clientes e começar a emitir pedidos de venda.
          </p>
        </div>

        <div class="d-flex align-items-center gap-3">
          <button
            type="button"
            class="btn nb-btn-guide-toggle"
            @click="apiGuiaUsuario"
          >
            <i class="fa" :class="showGuia ? 'fa-eye-slash' : 'fa-eye'"></i>
            <span>{{ showGuia ? 'Não exibir este guia ao logar' : 'Exibir este guia ao logar' }}</span>
          </button>
        </div>
      </div>

      <!-- PROGRESS BAR -->
      <div class="nb-progress-wrapper mt-4">
        <div class="d-flex justify-content-between align-items-center mb-1">
          <span class="nb-progress-label">
            Progresso de Configuração Comercial
          </span>
          <span class="nb-progress-percent">
            {{ completedCount }} de 4 etapas concluídas ({{ progressPercent }}%)
          </span>
        </div>
        <div class="progress nb-progress-bar-container">
          <div
            class="progress-bar nb-progress-bar-fill"
            role="progressbar"
            :style="{ width: progressPercent + '%' }"
            :aria-valuenow="progressPercent"
            aria-valuemin="0"
            aria-valuemax="100"
          ></div>
        </div>
      </div>
    </div>

    <!-- 4 STEP CHECKLIST CARDS -->
    <div class="row mb-5">
      <div
        class="col-xl-3 col-md-6 col-12 mb-4"
        v-for="(item, index) in etapasGuia"
        :key="index"
      >
        <div class="nb-step-card" :class="{ 'step-completed': item.completed }">
          <div class="nb-step-card-header d-flex align-items-center justify-content-between">
            <div class="d-flex align-items-center">
              <span class="nb-step-number mr-2">{{ index + 1 }}</span>
              <span v-if="item.completed" class="badge badge-success-soft">
                <i class="fa fa-check mr-1"></i> Concluído
              </span>
              <span v-else class="badge badge-pending-soft">
                <i class="fa fa-clock-o mr-1"></i> Pendente
              </span>
            </div>
            <button
              v-if="item.video"
              :id="item.name + '_video'"
              class="btn nb-btn-video ml-auto"
              @click="handleShowVideo(item)"
              title="Assistir tutorial em vídeo"
              aria-label="Assistir tutorial em vídeo"
            >
              <i class="fa fa-youtube-play"></i>
            </button>
          </div>

          <!-- STEP SVG ILLUSTRATION -->
          <div class="nb-step-illustration mt-3 mb-2">
            <guia-step-svg :step="item.svgStep" :completed="item.completed"></guia-step-svg>
          </div>

          <div class="nb-step-card-body">
            <h5 class="nb-step-title">{{ item.title }}</h5>
            <p class="nb-step-desc">
              {{ item.desc }}
            </p>
          </div>

          <div class="nb-step-card-footer">
            <button
              class="btn btn-block"
              :class="item.completed ? 'nb-btn-view' : 'nb-btn-action'"
              @click="handleFazerAgora(item)"
            >
              <i class="fa" :class="item.completed ? 'fa-check' : 'fa-arrow-right'" aria-hidden="true"></i>
              <span class="ml-1">{{ item.completed ? 'Configuração Concluída' : 'Configurar Agora' }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- SEÇÃO: VÍDEOS E TREINAMENTOS COMERCIAIS -->
    <div class="nb-section-header mb-3">
      <h4 class="nb-section-title">
        <i class="fa fa-video-camera text-primary mr-2"></i> Tutoriais e Treinamentos Comerciais
      </h4>
      <p class="nb-section-subtitle">
        Aprenda a cadastrar tabelas de preços, controlar grades de produtos, gerenciar descontos e emitir pedidos com facilidade.
      </p>
    </div>

    <div class="row">
      <div
        class="col-xl-4 col-md-6 col-12 mb-3"
        v-for="(item, index) in tutoriaisFV"
        :key="index"
      >
        <div class="nb-tutorial-card">
          <div class="d-flex align-items-start justify-content-between p-3">
            <div
              class="d-flex align-items-center nb-tutorial-link"
              @click="handleShowVideo(item)"
              @keyup.enter="handleShowVideo(item)"
              role="button"
              tabindex="0"
              title="Assistir tutorial em vídeo"
            >
              <div class="nb-tutorial-icon mr-3">
                <i class="fa fa-youtube-play text-danger"></i>
              </div>
              <div>
                <h6 class="nb-tutorial-title mb-1">{{ item.title }}</h6>
                <span class="nb-tutorial-tag" v-if="item.subtitle">{{ item.subtitle }}</span>
              </div>
            </div>
          </div>
          <div class="nb-tutorial-footer p-2 d-flex justify-content-between align-items-center">
            <button
              class="btn btn-sm nb-btn-play-sm"
              @click="handleShowVideo(item)"
            >
              <i class="fa fa-play mr-1"></i> Assistir Vídeo
            </button>
            <button
              v-if="item.url"
              class="btn btn-sm nb-btn-open-sm"
              @click="handleFazerAgora(item)"
            >
              Abrir tela <i class="fa fa-external-link ml-1"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- MODAL DE VÍDEO -->
    <transition name="fade">
      <div v-if="currVideo" class="nb-video-backdrop" @click="handleCloseVideo">
        <div class="nb-video-modal shadow-lg" @click.stop>
          <div class="nb-video-modal-header d-flex justify-content-between align-items-center px-3 py-3">
            <div class="d-flex align-items-center">
              <div class="nb-modal-header-icon mr-2">
                <i class="fa fa-play-circle text-primary"></i>
              </div>
              <h5 class="nb-modal-header-title mb-0">Tutorial em Vídeo</h5>
            </div>
            <button
              type="button"
              class="close nb-modal-close"
              @click="handleCloseVideo"
              aria-label="Fechar"
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="p-3">
            <div class="embed-responsive embed-responsive-16by9 rounded overflow-hidden position-relative nb-video-frame-container">
              <transition name="fade">
                <div v-if="isVideoLoading" class="nb-video-loading">
                  <div class="nb-video-spinner-wrapper mb-3">
                    <i class="fa fa-circle-o-notch fa-spin nb-video-spinner"></i>
                  </div>
                  <span class="nb-video-loading-title">Carregando tutorial...</span>
                  <span class="nb-video-loading-subtitle">Aguarde um instante</span>
                </div>
              </transition>
              <iframe
                class="embed-responsive-item"
                :src="currVideo"
                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen
                @load="onVideoLoad"
              ></iframe>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { apiORDSapp } from "../shared/api-functions";
import { SysVars } from "../main";
import GuiaStepSvg from "./guia-step-svg.vue";

export default {
  name: "dashboard-guia-forcavendas",

  components: {
    GuiaStepSvg
  },

  data() {
    return {
      etapasGuia: [
        {
          completed: false,
          name: "guiafv_empresa",
          svgStep: "guianfe_empresa",
          title: "Configure os dados da sua empresa",
          subtitle: "empresa",
          desc: "Defina Razão Social, CNPJ, telefone, endereço e informações da sua empresa.",
          video: "//www.youtube.com/embed/0QMVqSEm-ww",
          url: "/guiaempresa"
        },
        {
          completed: false,
          name: "guiafv_item",
          svgStep: "guianfe_item",
          title: "Cadastre produtos e serviços",
          subtitle: "produto",
          desc: "Cadastre seu catálogo com descrição, preço de venda, fotos, estoque e unidade de medida.",
          video: "//www.youtube.com/embed/c241at1ws14",
          url: "/item/novo"
        },
        {
          completed: false,
          name: "guiafv_part",
          svgStep: "guianfe_part",
          title: "Cadastre clientes e contatos",
          subtitle: "cliente",
          desc: "Cadastre sua carteira de clientes com contato telefônico para emissão de orçamentos e pedidos.",
          video: "//www.youtube.com/embed/Rb8ygl4dAqk",
          url: "/part/novo"
        },
        {
          completed: false,
          name: "guiafv_venda",
          svgStep: "guianfe_venda",
          title: "Emita seu primeiro pedido de venda",
          subtitle: "venda",
          desc: "Gere orçamentos e pedidos com cálculo de descontos, tabelas de preço e envio por WhatsApp ou e-mail.",
          video: "//www.youtube.com/embed/QJC0ZLvBavc",
          url: "/pedido/novo"
        }
      ],

      tutoriaisFV: [
        {
          title: "Como lançar um pedido de venda",
          subtitle: "Vendas",
          video: "//www.youtube.com/embed/QJC0ZLvBavc",
          url: "/pedido/novo"
        },
        {
          title: "Cadastro de produtos",
          subtitle: "Produtos",
          video: "//www.youtube.com/embed/c241at1ws14",
          url: "/item/novo"
        },
        {
          title: "Grades de produtos",
          subtitle: "Produtos",
          video: "//www.youtube.com/embed/Ds6DmZ7uzEg",
          url: "/fv/produtos"
        },
        {
          title: "Atribuir grade ao produto",
          subtitle: "Produtos",
          video: "//www.youtube.com/embed/BogRiM2uBxM",
          url: "/fv/produtos"
        },
        {
          title: "Tabelas de preços",
          subtitle: "Comercial",
          video: "//www.youtube.com/embed/CFIBJw78Auc",
          url: "/fv/produtos"
        },
        {
          title: "Tabelas de desconto",
          subtitle: "Comercial",
          video: "//www.youtube.com/embed/atVVQ1Gp3PM",
          url: "/fv/produtos"
        },
        {
          title: "Cadastro de clientes",
          subtitle: "Clientes",
          video: "//www.youtube.com/embed/Rb8ygl4dAqk",
          url: "/part/novo"
        },
        {
          title: "Cadastrar novos usuários e vendedores",
          subtitle: "Configurações",
          video: "//www.youtube.com/embed/kbZ9kTtajIA",
          url: "/configuracoes"
        },
        {
          title: "Como configurar regras de ICMS e substituição tributária",
          subtitle: "Tributação",
          video: "//www.youtube.com/embed/gjwdNe3q8AM",
          url: "/regraimplist"
        },
        {
          title: "Como importar clientes e fornecedores",
          subtitle: "Importação",
          video: "//www.youtube.com/embed/ql3fiqp-Lvk",
          url: "/importa/clientes"
        },
        {
          title: "Como importar o cadastro de produtos",
          subtitle: "Importação",
          video: "//www.youtube.com/embed/6Bmo4YZTmII",
          url: "/importa/produtos"
        }
      ],

      currVideo: null,
      isVideoLoading: false,
      videoLoadTimeout: null
    };
  },

  computed: {
    sessao: function() {
      return this.$store.getters.getSessao;
    },

    dadosGuia: function() {
      return this.$store.getters.getGuiaNFe || {};
    },

    showGuia: function() {
      return SysVars.guia;
    },

    completedCount: function() {
      return this.etapasGuia.filter(item => item.completed).length;
    },

    progressPercent: function() {
      return Math.round((this.completedCount / 4) * 100);
    }
  },

  methods: {
    handleShowVideo(item) {
      this.isVideoLoading = true;
      this.currVideo = item.video;
      if (this.videoLoadTimeout) {
        clearTimeout(this.videoLoadTimeout);
      }
      this.videoLoadTimeout = setTimeout(() => {
        this.isVideoLoading = false;
      }, 7000);
    },

    handleCloseVideo() {
      this.currVideo = null;
      this.isVideoLoading = false;
      if (this.videoLoadTimeout) {
        clearTimeout(this.videoLoadTimeout);
      }
    },

    onVideoLoad() {
      this.isVideoLoading = false;
      if (this.videoLoadTimeout) {
        clearTimeout(this.videoLoadTimeout);
      }
    },

    handleFazerAgora(item) {
      if (item.url) {
        this.$router.push(item.url);
      }
    },

    async loadData() {
      await this.$store.dispatch("loadSessao", this);
      await this.$store.dispatch("loadGuiaNFe", this);

      this.etapasGuia[0].completed = !!this.dadosGuia.empresa;
      this.etapasGuia[1].completed = !!this.dadosGuia.item;
      this.etapasGuia[2].completed = !!this.dadosGuia.cliente;
      this.etapasGuia[3].completed = !!(this.dadosGuia.pedido || this.dadosGuia.varejo);
    },

    async apiGuiaUsuario() {
      await apiORDSapp(this, "usuario/guia/", "post", null);
      this.$appLoading(false);
      if (SysVars.guia) {
        this.$erpToastSuccess("Guia desativado na tela inicial.");
      } else {
        this.$erpToastSuccess("Guia ativado na tela inicial.");
      }
      SysVars.guia = !SysVars.guia;
    }
  },

  async mounted() {
    await this.loadData();
    this.$nextTick(function() {
      if (window.jQuery) {
        window.jQuery('[data-toggle="tooltip"]').tooltip();
      }
    });
  }
};
</script>

<style scoped>
.nb-dashboard-guia {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  color: #0b1c30;
}

/* HERO PROGRESS CARD */
.nb-hero-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 24px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.03);
}

.nb-hero-icon {
  font-size: 20px;
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: #eff4ff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.nb-hero-title {
  font-size: 20px;
  font-weight: 700;
  color: #0b1c30;
  letter-spacing: -0.2px;
}

.nb-hero-subtitle {
  font-size: 13.5px;
  color: #64748b;
  margin-top: 4px;
  max-width: 820px;
}

.nb-btn-guide-toggle {
  background: #f8fafc;
  color: #475569;
  border: 1px solid #cbd5e1;
  font-size: 12.5px;
  font-weight: 600;
  padding: 8px 14px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.15s ease;
}

.nb-btn-guide-toggle:hover {
  background: #f1f5f9;
  color: #0b1c30;
  border-color: #94a3b8;
}

/* PROGRESS BAR */
.nb-progress-wrapper {
  background: #f8fafc;
  border: 1px solid #f1f5f9;
  border-radius: 10px;
  padding: 12px 16px;
}

.nb-progress-label {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
}

.nb-progress-percent {
  font-size: 12.5px;
  font-weight: 700;
  color: #004e94;
}

.nb-progress-bar-container {
  height: 8px;
  border-radius: 6px;
  background-color: #e2e8f0;
  overflow: hidden;
}

.nb-progress-bar-fill {
  background: linear-gradient(90deg, #004e94 0%, #00875a 100%);
  transition: width 0.4s ease;
}

/* 4 STEP CARDS */
.nb-step-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 16px;
  position: relative;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
}

.nb-step-card:hover {
  border-color: #cbd5e1;
  box-shadow: 0 4px 14px rgba(0, 78, 148, 0.06);
  transform: translateY(-2px);
}

.nb-step-card.step-completed {
  border-color: #c6f6d5;
  background: #fcfffd;
}

.nb-step-number {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #f1f5f9;
  color: #475569;
  font-size: 12px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.badge-success-soft {
  background: #ecfdf5;
  color: #059669;
  font-weight: 600;
  font-size: 11px;
  padding: 4px 8px;
  border-radius: 6px;
}

.badge-pending-soft {
  background: #fffbeb;
  color: #b45309;
  font-weight: 600;
  font-size: 11px;
  padding: 4px 8px;
  border-radius: 6px;
}

.nb-btn-video {
  color: #ef4444;
  font-size: 20px;
  padding: 0;
  line-height: 1;
  background: transparent;
  border: none;
  transition: transform 0.15s ease;
}

.nb-btn-video:hover {
  transform: scale(1.15);
  color: #dc2626;
}

.nb-step-illustration {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 130px;
}

.nb-step-title {
  font-size: 15px;
  font-weight: 700;
  color: #0b1c30;
  margin-bottom: 6px;
  line-height: 1.3;
}

.nb-step-desc {
  font-size: 12.5px;
  color: #64748b;
  margin-bottom: 16px;
  line-height: 1.45;
  min-height: 52px;
}

.nb-step-card-footer {
  margin-top: auto;
}

.nb-btn-action {
  background: #004e94;
  color: #ffffff;
  font-weight: 600;
  font-size: 13px;
  padding: 9px 14px;
  border-radius: 8px;
  border: 1px solid #004e94;
  transition: all 0.15s ease;
}

.nb-btn-action:hover {
  background: #00376c;
  border-color: #00376c;
  color: #ffffff;
}

.nb-btn-view {
  background: #ecfdf5;
  color: #059669;
  font-weight: 600;
  font-size: 13px;
  padding: 9px 14px;
  border-radius: 8px;
  border: 1px solid #a7f3d0;
  transition: all 0.15s ease;
}

.nb-btn-view:hover {
  background: #d1fae5;
  color: #047857;
}

/* SECTION HEADER */
.nb-section-header {
  border-bottom: 1px solid #e2e8f0;
  padding-bottom: 12px;
}

.nb-section-title {
  font-size: 17px;
  font-weight: 700;
  color: #0b1c30;
}

.nb-section-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-bottom: 0;
}

/* TUTORIAL CARDS */
.nb-tutorial-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: all 0.15s ease;
}

.nb-tutorial-card:hover {
  border-color: #cbd5e1;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.04);
}

.nb-tutorial-link {
  cursor: pointer;
  width: 100%;
}

.nb-tutorial-icon {
  font-size: 26px;
  line-height: 1;
}

.nb-tutorial-title {
  font-size: 13.5px;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.35;
}

.nb-tutorial-tag {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  background: #f1f5f9;
  padding: 2px 6px;
  border-radius: 4px;
}

.nb-tutorial-footer {
  margin-top: auto;
  background: #f8fafc;
  border-top: 1px solid #f1f5f9;
  border-radius: 0 0 10px 10px;
}

.nb-btn-play-sm {
  background: #004e94;
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  border-radius: 6px;
  padding: 4px 10px;
}

.nb-btn-play-sm:hover {
  background: #00376c;
  color: #ffffff;
}

.nb-btn-open-sm {
  background: #ffffff;
  color: #475569;
  border: 1px solid #cbd5e1;
  font-size: 12px;
  font-weight: 600;
  border-radius: 6px;
  padding: 4px 10px;
}

.nb-btn-open-sm:hover {
  background: #f1f5f9;
  color: #0b1c30;
}

/* VIDEO MODAL */
.nb-video-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(11, 28, 48, 0.65);
  backdrop-filter: blur(4px);
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.nb-video-modal {
  background: #ffffff;
  border-radius: 14px;
  max-width: 820px;
  width: 100%;
  overflow: hidden;
}

.nb-video-modal-header {
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
}

.nb-modal-header-icon {
  font-size: 22px;
  line-height: 1;
}

.nb-modal-header-title {
  font-size: 16px;
  font-weight: 700;
  color: #0b1c30;
}

.nb-modal-close {
  font-size: 24px;
  color: #64748b;
  opacity: 0.8;
  outline: none;
}

.nb-modal-close:hover {
  color: #0b1c30;
  opacity: 1;
}

.nb-video-frame-container {
  background: #0b1c30;
  min-height: 400px;
}

.nb-video-loading {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #0b1c30;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 2;
  color: #ffffff;
}

.nb-video-spinner {
  font-size: 38px;
  color: #38bdf8;
}

.nb-video-loading-title {
  font-size: 15px;
  font-weight: 600;
  margin-top: 8px;
}

.nb-video-loading-subtitle {
  font-size: 12px;
  color: #94a3b8;
}
</style>



// WEBPACK FOOTER //
// src/dashboards/dashboard-guia-forcavendas.vue